import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ADD_CP_ADMIN_USER, CREATE_CP_ADMIN_MULTI_STATUS, UPDATE_CP_ADMIN_MULTI_STATUS, DELETE_CP_ADMIN_MULTI_STATUS, DELETE_CP_ADMIN_USER, EDIT_CP_ADMIN_USER, GET_COMPANY_VERIFICATION_INFO, GET_CP_ADMIN_MULTI_STATUS, GET_CP_ADMIN_USER, GET_CP_ADMIN_USER_BY_ID, GET_CP_ADMIN_VERIFY_USER, GET_INVENTORY, GET_INVENTORY_AUTOCOMPLETE, GET_RESOURCES_CATEGORY, GET_RESOURCES_CATEGORY_DETAILS, GET_RESOURCES_BY_CATEGORY, GET_RESOURCES_BY_ITEM, GET_USER_PROFILE_BY_ID, POST_CP_ADMIN_VERIFY_USER, SEND_TEMP_PASSWORD, UPDATE_USER_PASSWORD, UPDATE_USER_PROFILE, UPLOAD_INVENTORY, GET_LEADTIME_LISTPRICE, USER_PERMISSION,
  GET_CHANNEL_PARTNERS, GET_CHANNEL_PARTNERS_BY_ID, GET_CHANNEL_PARTNERS_USERS_BY_ID, GET_ALL_COMPANIES, GET_COMPANY_BY_ID, GET_COMPANY_USERS_BY_ID, GET_CHANNEL_PARTNER_ACCESS_STATS
 } from '../../services/URLs'
import useRequest from '../useRequest'
import { generatePath } from 'react-router-dom'

const QUERIES = {
  USER_PERMISSION: 'USER_PERMISSION',
  CP_ADMIN_USER: 'CP_ADMIN_USER',
  CP_ADMIN_USER_BY_ID: 'CP_ADMIN_USER_BY_ID',
  CP_ADMIN_VERIFY_USER: 'CP_ADMIN_VERIFY_USER',
  CP_ADMIN_COMPANY_VERIFICATION_INFO: 'CP_ADMIN_COMPANY_VERIFICATION_INFO',
  CP_ADMIN_MULTI_STATUS: 'CP_ADMIN_MULTI_STATUS',
  USER_PROFILE_BY_ID: 'USER_PROFILE_BY_ID',
  RESOURCE_CAT:'RESOURCE_CAT',
  RESOURCE_CAT_DETAILS:'RESOURCE_CAT_DETAILS',
  RESOURCES_BY_CATEGORY: 'RESOURCES_BY_CATEGORY',
  RESOURCES_BY_ITEM: 'RESOURCES_BY_ITEM',
  GET_INVENTORY: 'GET_INVENTORY',
  GET_INVENTORY_AUTOCOMPLETE: 'GET_INVENTORY_AUTOCOMPLETE',
  GET_LEADTIME_LISTPRICE: 'GET_LEADTIME_LISTPRICE',
  GET_CHANNEL_PARTNERS: 'GET_CHANNEL_PARTNERS',
  GET_CHANNEL_PARTNERS_BY_ID: 'GET_CHANNEL_PARTNERS_BY_ID',
  GET_CHANNEL_PARTNERS_USERS_BY_ID: 'GET_CHANNEL_PARTNERS_USERS_BY_ID',
  GET_ALL_COMPANIES: 'GET_ALL_COMPANIES',
  GET_COMPANY_BY_ID: 'GET_COMPANY_BY_ID',
  GET_COMPANY_USERS_BY_ID: 'GET_COMPANY_USERS_BY_ID',
  GET_CHANNEL_PARTNER_ACCESS_STATS: 'GET_CHANNEL_PARTNER_ACCESS_STATS',
}

const MUTATIONS = {
  CP_ADMIN_USER_ADD: 'CP_ADMIN_USER_ADD',
  CP_ADMIN_USER_EDIT: 'CP_ADMIN_USER_EDIT',
  CP_ADMIN_USER_DELETE: 'CP_ADMIN_USER_DELETE',
  CP_ADMIN_VERIFY_USER: 'CP_ADMIN_VERIFY_USER',
  CP_ADMIN_USER_SEND_TEMP_PASSWORD: 'CP_ADMIN_USER_SEND_TEMP_PASSWORD',
  USER_PROFILE_UPDATE: 'USER_PROFILE_UPDATE',
  USER_PASSWORD_UPDATE: 'USER_PASSWORD_UPDATE',
  DELETE_CP_ADMIN_MULTI_STATUS: 'DELETE_CP_ADMIN_MULTI_STATUS',
  CREATE_CP_ADMIN_MULTI_STATUS: 'CREATE_CP_ADMIN_MULTI_STATUS',
  UPDATE_CP_ADMIN_MULTI_STATUS: 'UPDATE_CP_ADMIN_MULTI_STATUS',
  UPLOAD_INVENTORY: 'UPLOAD_INVENTORY',
}

interface CPAdminUserPayload {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  fax: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  uploadInventory: boolean;
  enterOrders: boolean;
}

interface CPAdminUserPayloadWithId extends CPAdminUserPayload {
  userId: string;
}

interface CPAdminUserPayloadExtended extends Omit<CPAdminUserPayload, 'uploadInventory' | 'enterOrders'> {
  userId: string;
}

interface CPAdminMultiStatusPayload {
  email: string;
  acct1: string;
  acct2: string;
  acct3: string;
  acct4: string;
  acct5: string;
  acct6: string;
  acct7: string;
  acct8: string;
}

interface CPAdminMultiStatusUpdatePayload extends CPAdminMultiStatusPayload {
  recordId: number;
}

interface GetInventoryParams {
  partNo?: string;
  exactSearch?: boolean;
}

export const useGetUserPermission = (enabled = true, selectedExtranet?: string) => {
  const request = useRequest();
  
  return useQuery({
    queryKey: [QUERIES.USER_PERMISSION, selectedExtranet],
    queryFn: async () => {
      const params = selectedExtranet ? { selectedExtranet } : {};
      return await request({
        method: 'get',
        url: USER_PERMISSION,
        config: { params },
        completeResponse: false,
      })
    },
    enabled: enabled,
    staleTime: 0, // Always consider data stale to ensure fresh fetch when extranet changes
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
  })
}

export const useGetCPAdminUser = () => {
  const request = useRequest();
  
  return useQuery({
    queryKey: [QUERIES.CP_ADMIN_USER],
    queryFn: async () => {
      return await request({
        method: 'get',
        url: GET_CP_ADMIN_USER,
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useCreateCPAdminUser = () => {
  const request = useRequest();
  
  return useMutation({
    mutationKey: [MUTATIONS.CP_ADMIN_USER_ADD],
    mutationFn: async (payload: CPAdminUserPayload) => {
      return await request({
        method: 'post',
        url: ADD_CP_ADMIN_USER,
        payload,
        config: { headers: {} },
        completeResponse: true,
      });
    },
  });
}

export const useUpdateCPAdminUser = () => {
  const request = useRequest();
  
  return useMutation({
    mutationKey: [MUTATIONS.CP_ADMIN_USER_EDIT],
    mutationFn: async (payload: CPAdminUserPayloadWithId) => {
      return await request({
        method: 'put',
        url: generatePath(EDIT_CP_ADMIN_USER, { userId: payload.userId }),
        payload,
        config: { headers: {} },
        completeResponse: true,
      });
    },
  });
}

export const useGetCPAdminUserById = (userId: string | undefined) => {
  const request = useRequest();
  return useQuery({
    queryKey: [QUERIES.CP_ADMIN_USER_BY_ID],
    queryFn: async () => {
      if (!userId) {
        return {}
      }
      return await request({
        method: 'get',
        url: generatePath(GET_CP_ADMIN_USER_BY_ID, { userId: userId }),
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useDeleteCPAdminUser = () => {
  const request = useRequest();
  
  return useMutation({
    mutationKey: [MUTATIONS.CP_ADMIN_USER_DELETE],
    mutationFn: async ({ userId }: { userId: string }) => {
      return await request({
        method: 'delete',
        url: generatePath(DELETE_CP_ADMIN_USER, { userId: userId }),
        config: { headers: {} },
        completeResponse: true,
      });
    },
  });
}

export const useGetCPAdminCompanyVerificationInfo = () => {
  const request = useRequest();
  
  return useQuery({
    queryKey: [QUERIES.CP_ADMIN_COMPANY_VERIFICATION_INFO],
    queryFn: async () => {
      return await request({
        method: 'get',
        url: GET_COMPANY_VERIFICATION_INFO,
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useGetCPAdminVerifyUser = () => {
  const request = useRequest();
  
  return useQuery({
    queryKey: [QUERIES.CP_ADMIN_VERIFY_USER],
    queryFn: async () => {
      return await request({
        method: 'get',
        url: GET_CP_ADMIN_VERIFY_USER,
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const usePostCPAdminVerifyUser = () => {
  const request = useRequest();
  
  return useMutation({
    mutationKey: [MUTATIONS.CP_ADMIN_VERIFY_USER],
    mutationFn: async () => {
      return await request({
        method: 'post',
        url: POST_CP_ADMIN_VERIFY_USER,
        config: {},
        completeResponse: true,
      })
    },
  })
}

export const useSendCPAdminUserTempPassword = () => {
  const request = useRequest();
  
  return useMutation({
    mutationKey: [MUTATIONS.CP_ADMIN_USER_SEND_TEMP_PASSWORD],
    mutationFn: async (payload: { email: string }) => {
      return await request({
        method: 'post',
        url: SEND_TEMP_PASSWORD,
        payload,
        config: {},
        completeResponse: true,
      })
    },
  })
}

export const useGetCPAdminMultiStatus = () => {
  const request = useRequest();
  
  return useQuery({
    queryKey: [QUERIES.CP_ADMIN_MULTI_STATUS],
    queryFn: async () => {
      return await request({
        method: 'get',
        url: GET_CP_ADMIN_MULTI_STATUS,
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useDeleteCPAdminMultiStatus = () => {
  const request = useRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MUTATIONS.DELETE_CP_ADMIN_MULTI_STATUS],
    mutationFn: async (payload: string[]) => {
      return await request({
        method: 'delete',
        url: DELETE_CP_ADMIN_MULTI_STATUS,
        payload,
        config: {},
        completeResponse: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERIES.CP_ADMIN_MULTI_STATUS] });
    },
  })
}

export const useCreateCPAdminMultiStatus = () => {
  const request = useRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MUTATIONS.CREATE_CP_ADMIN_MULTI_STATUS],
    mutationFn: async (payload: CPAdminMultiStatusPayload) => {
      return await request({
        method: 'post',
        url: CREATE_CP_ADMIN_MULTI_STATUS,
        payload,
        config: {},
        completeResponse: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERIES.CP_ADMIN_MULTI_STATUS] });
    },
  })
}

export const useUpdateCPAdminMultiStatus = () => {
  const request = useRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MUTATIONS.UPDATE_CP_ADMIN_MULTI_STATUS],
    mutationFn: async (payload: CPAdminMultiStatusUpdatePayload) => {
      return await request({
        method: 'put',
        url: generatePath(UPDATE_CP_ADMIN_MULTI_STATUS, { recordId: payload.recordId.toString() }),
        payload: {
          email: payload.email,
          acct1: payload.acct1,
          acct2: payload.acct2,
          acct3: payload.acct3,
          acct4: payload.acct4,
          acct5: payload.acct5,
          acct6: payload.acct6,
          acct7: payload.acct7,
          acct8: payload.acct8
        },
        config: {},
        completeResponse: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERIES.CP_ADMIN_MULTI_STATUS] });
    },
  })
}

export const useGetUserProfileById = (userId: string | undefined) => {
  const request = useRequest();
  return useQuery({
    queryKey: [QUERIES.USER_PROFILE_BY_ID, userId],
    queryFn: async () => {
      if (!userId) {
        return {};
      }
      return await request({
        method: 'get',
        url: generatePath(GET_USER_PROFILE_BY_ID, { userId }),
        config: {},
        completeResponse: false,
      });
    },
  });
}

export const useUpdateUserProfile = () => {
  const request = useRequest();
  return useMutation({
    mutationKey: [MUTATIONS.USER_PROFILE_UPDATE],
    mutationFn: async (payload: CPAdminUserPayloadExtended) => {
      return await request({
        method: 'put',
        url: generatePath(UPDATE_USER_PROFILE, { userId: payload.userId }),
        payload,
        config: {},
        completeResponse: true,
      });
    },
  });
};

export const useUpdateUserPassword = (userId: string) => {
  const request = useRequest();
  
  return useMutation({
    mutationKey: [MUTATIONS.USER_PASSWORD_UPDATE],
    mutationFn: async (payload: { currentPassword: string, newPassword: string, confirmPassword: string }) => {
      return await request({
        method: 'post',
        url: generatePath(UPDATE_USER_PASSWORD, { userId: userId }),
        payload,
        config: {},
        completeResponse: true,
      })
    },
  })
}

export const useGetResourcesCategory = () => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.RESOURCE_CAT],
    queryFn: async () => {
      return await request({
        method: 'get',
        url: GET_RESOURCES_CATEGORY,
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useGetResourcesCategoryDetails = (itemId: string | undefined) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.RESOURCE_CAT_DETAILS, itemId],
    queryFn: async () => {
      if (!itemId) {
          return {};
        }
      return await request({
        method: 'get',
        url: generatePath(GET_RESOURCES_CATEGORY_DETAILS, { itemId }),
        config: {},
        completeResponse: false,
      });
    },
  })
}

export const useGetResourcesByCategory = (categoryName: string | undefined) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.RESOURCES_BY_CATEGORY, categoryName],
    queryFn: async () => {
      if (!categoryName) {
        return {};
      }
      return await request({
        method: 'get',
        url: generatePath(GET_RESOURCES_BY_CATEGORY, { categoryName: encodeURIComponent(categoryName.trim()) }),
        config: {},
        completeResponse: false,
      });
    },
  });
};

export const useGetResourcesByItem = (itemId: string | undefined) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.RESOURCES_BY_ITEM, itemId],
    queryFn: async () => {
      if (!itemId) {
        return {};
      }
      return await request({
        method: 'get',
        url: generatePath(GET_RESOURCES_BY_ITEM, { itemId: itemId }),
        config: {},
        completeResponse: false,
      });
    },
  });
};

export const useUploadInventory = () => {
  const request = useRequest();

  return useMutation({
    mutationKey: [MUTATIONS.UPLOAD_INVENTORY],
    mutationFn: async (formData: FormData) => {
      return await request({
        method: 'post',
        url: UPLOAD_INVENTORY,
        payload: formData,
        config: {},
        completeResponse: true,
      })
    },
  })
}

export const useGetInventory = (params?: GetInventoryParams) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.GET_INVENTORY, params],
    queryFn: async (payload) => {
      if (!params || !params.partNo) {
        return [];
      }
      
      // Replace * with % for wildcard search
      const processedPartNo = params.partNo.replace(/\*/g, '%');
      
      return await request({
        method: 'get',
        url: GET_INVENTORY,
        payload,
        config: {
          params: {
            partNo: processedPartNo,
            ...(params.exactSearch !== undefined && { exactSearch: params.exactSearch }),
          },
        },
        completeResponse: false,
      })
    },
  })
}

export const useGetLeadTimeListPrice = (
  params?: GetInventoryParams
) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.GET_LEADTIME_LISTPRICE, params?.partNo],
    queryFn: async () => {
      if (!params?.partNo) {
        return [];
      }

      // Replace * with % for wildcard search
      const processedPartNo = params.partNo.replace(/\*/g, '%');

      return await request({
        method: 'get',
        url: GET_LEADTIME_LISTPRICE,
        config: {
          params: {
            partno: encodeURIComponent(processedPartNo), // must match the API exactly
          },
        },
        completeResponse: false,
      });
    },
    enabled: !!params?.partNo,
  });
};

export const useGetChannelPartners = () => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.GET_CHANNEL_PARTNERS],
    queryFn: async () => {
      return await request({
        method: 'get',
        url: GET_CHANNEL_PARTNERS,
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useGetAllCompanies = () => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.GET_ALL_COMPANIES],
    queryFn: async () => {
      return await request({
        method: 'get',
        url: GET_ALL_COMPANIES,
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useGetCompanyById = (id: string | undefined) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.GET_COMPANY_BY_ID, id],
    queryFn: async () => {
      if (!id) {
        return {}
      }
      return await request({
        method: 'get',
        url: generatePath(GET_COMPANY_BY_ID, { id: id }),
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useGetCompanyUsersById = (id: string | undefined) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.GET_COMPANY_USERS_BY_ID, id],
    queryFn: async () => {
      if (!id) {
        return {}
      }
      return await request({
        method: 'get',
        url: generatePath(GET_COMPANY_USERS_BY_ID, { id: id }),
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useGetChannelPartnersById = (id: string | undefined) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.GET_CHANNEL_PARTNERS_BY_ID],
    queryFn: async () => {
      if (!id) {
        return {}
      }
      return await request({
        method: 'get',
        url: generatePath(GET_CHANNEL_PARTNERS_BY_ID, { id: id }),
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useGetChannelPartnersUsersById = (id: string | undefined) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.GET_CHANNEL_PARTNERS_USERS_BY_ID],
    queryFn: async () => {
      if (!id) {
        return {}
      }
      return await request({
        method: 'get',
        url: generatePath(GET_CHANNEL_PARTNERS_USERS_BY_ID, { id: id }),
        config: {},
        completeResponse: false,
      })
    },
  })
}

export const useGetInventoryAutocomplete = (prefix: string) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERIES.GET_INVENTORY_AUTOCOMPLETE, prefix],
    queryFn: async () => {
      // Always trigger API for any input above 2 characters, no upper limit
      if (!prefix || prefix.trim().length < 2) {
        return { partNumbers: [] };
      }
      
      // Replace * with % for wildcard search
      const processedPrefix = prefix.trim().replace(/\*/g, '%');
      
      return await request({
        method: 'get',
        url: GET_INVENTORY_AUTOCOMPLETE,
        config: {
          params: { prefix: processedPrefix },
        },
        completeResponse: false,
      });
    },
    enabled: !!prefix && prefix.trim().length >= 2, // No upper limit
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
  })
}

export const useGetChannelPartnerAccessStats = () => {
  const request = useRequest();
  return useQuery({
    queryKey: [QUERIES.GET_CHANNEL_PARTNER_ACCESS_STATS],
    queryFn: async () => {
      return await request({
        method: 'get',
        url: GET_CHANNEL_PARTNER_ACCESS_STATS,
        config: {},
        completeResponse: false,
      })
    },
    enabled: false, // Disable auto-fetch, will be enabled manually
  })
}